import { Star, Phone, Mail, MapPin, Clock, Check, ChevronRight } from "lucide-react";
import EditableText from "@/components/EditableText";
import { useGoogleFont } from "@/components/useGoogleFont";
import { getImages } from "@/constants/categoryImages";

function clone(o) { return JSON.parse(JSON.stringify(o)); }
function setByPath(obj, path, value) {
  let cur = obj;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  cur[path[path.length - 1]] = value;
}

function Rating({ n = 5, color }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={16} fill={i < n ? color : "none"} color={i < n ? color : "#d4d4d8"} />
      ))}
    </div>
  );
}

export default function WebsiteRenderer({ website, profile, editable = false, onChange, vp = "desktop" }) {
  const colors = website.colors || {};
  const fonts = website.fonts || {};
  useGoogleFont([fonts.heading, fonts.body]);

  const primary = colors.primary || "#111111";
  const accent = colors.accent || primary;
  const bg = colors.bg || "#ffffff";
  const text = colors.text || "#111111";
  const secondary = colors.secondary || "#f4f4f5";
  const headingFont = fonts.heading ? `'${fonts.heading}', sans-serif` : "'Outfit', sans-serif";
  const bodyFont = fonts.body ? `'${fonts.body}', sans-serif` : "'Manrope', sans-serif";

  const catKey = profile?.profile?.business?.category_key || profile?.business?.category_key || "generic";
  const bizPhotos = profile?.profile?.business?.photos || profile?.business?.photos || [];
  const owned = website.media || [];
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const abs = (u) => (u && u.startsWith("/api/") ? `${backend}${u}` : u);
  const ownedAbs = owned.map(abs);
  const gallery = [...ownedAbs, ...bizPhotos];
  const fallback = getImages(catKey);
  const imgs = [gallery[0] || fallback[0], gallery[1] || fallback[1], gallery[2] || fallback[2]];
  // A slot is "user-uploaded" only when it maps to an owned media item.
  const isOwnedSlot = (idx) => idx < ownedAbs.length && !!ownedAbs[idx];
  // Cascades through the category fallback list on load failure so a broken
  // Google Maps photo (404/hotlink-blocked) never shows a broken image icon.
  const onImgError = (e) => {
    const el = e.currentTarget;
    const next = parseInt(el.dataset.fbIdx || "-1", 10) + 1;
    if (next < fallback.length) {
      el.dataset.fbIdx = String(next);
      el.src = fallback[next];
    } else {
      el.onerror = null;
    }
  };

  const isMobile = vp === "mobile";
  const isTablet = vp === "tablet";
  const pad = isMobile ? "px-5" : "px-8";

  const patch = (path, value) => {
    if (!onChange) return;
    const c = clone(website);
    setByPath(c, path, value);
    onChange(c);
  };

  const sections = website.sections || [];
  const nav = website.nav || {};

  const gridCols = (max) => {
    const n = isMobile ? 1 : isTablet ? Math.min(2, max) : max;
    return { display: "grid", gridTemplateColumns: `repeat(${n}, minmax(0,1fr))`, gap: isMobile ? "16px" : "24px" };
  };

  const sectionWrap = (i, children, style = {}) => (
    <section key={i} style={{ paddingTop: isMobile ? 48 : 88, paddingBottom: isMobile ? 48 : 88, ...style }}>
      <div className={`mx-auto w-full max-w-6xl ${pad}`}>{children}</div>
    </section>
  );

  const renderSection = (s, i) => {
    switch (s.type) {
      case "hero":
        return (
          <section key={i} style={{ background: bg, borderBottom: `1px solid ${secondary}` }}>
            <div className={`mx-auto w-full max-w-6xl ${pad}`} style={{ paddingTop: isMobile ? 40 : 80, paddingBottom: isMobile ? 40 : 80 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.05fr 0.95fr", gap: isMobile ? 28 : 56, alignItems: "center" }}>
                <div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {(s.badges || []).map((b, j) => (
                      <span key={j} style={{ background: secondary, color: primary }} className="text-xs font-medium px-3 py-1.5 rounded-full">{b}</span>
                    ))}
                  </div>
                  <EditableText as="h1" editable={editable} value={s.headline}
                    onCommit={(v) => patch(["sections", i, "headline"], v)}
                    style={{ fontFamily: headingFont, color: text, fontSize: isMobile ? 32 : 52, lineHeight: 1.05, fontWeight: 700, letterSpacing: "-0.02em" }} />
                  <EditableText as="p" editable={editable} value={s.subheadline}
                    onCommit={(v) => patch(["sections", i, "subheadline"], v)}
                    style={{ fontFamily: bodyFont, color: "#52525b", fontSize: isMobile ? 16 : 19, marginTop: 18, lineHeight: 1.6 }} />
                  <div className="flex flex-wrap gap-3 mt-8">
                    <button style={{ background: primary, color: "#fff", fontFamily: bodyFont }} className="px-6 py-3 rounded-full font-semibold text-sm inline-flex items-center gap-2">
                      {s.cta_primary || "Get Started"} <ChevronRight size={16} />
                    </button>
                    {s.cta_secondary && (
                      <button style={{ borderColor: secondary, color: text, fontFamily: bodyFont }} className="px-6 py-3 rounded-full font-semibold text-sm border-2 bg-transparent">
                        {s.cta_secondary}
                      </button>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <img src={imgs[0]} onError={isOwnedSlot(0) ? undefined : onImgError} alt={nav.logo_text} className="w-full object-cover rounded-2xl" style={{ height: isMobile ? 220 : 420 }} />
                  <div className="absolute -bottom-4 -left-2 bg-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-2" style={{ border: `1px solid ${secondary}` }}>
                    <Rating n={5} color={accent} />
                    <span className="text-sm font-semibold" style={{ color: text }}>Trusted locally</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      case "trust":
        return (
          <section key={i} style={{ background: primary }}>
            <div className={`mx-auto w-full max-w-6xl ${pad}`} style={{ paddingTop: isMobile ? 28 : 40, paddingBottom: isMobile ? 28 : 40 }}>
              <div style={gridCols(Math.min(4, (s.items || []).length || 1))}>
                {(s.items || []).map((it, j) => (
                  <div key={j} className="text-center">
                    <div style={{ fontFamily: headingFont, color: "#fff", fontSize: isMobile ? 26 : 34, fontWeight: 700 }}>{it.value}</div>
                    <div style={{ color: "rgba(255,255,255,.65)", fontFamily: bodyFont }} className="text-sm mt-1">{it.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case "services":
        return sectionWrap(i, (
          <>
            <div className="max-w-2xl mb-10">
              <EditableText as="h2" editable={editable} value={s.title} onCommit={(v) => patch(["sections", i, "title"], v)}
                style={{ fontFamily: headingFont, color: text, fontSize: isMobile ? 26 : 38, fontWeight: 700, letterSpacing: "-0.02em" }} />
              <EditableText as="p" editable={editable} value={s.subtitle} onCommit={(v) => patch(["sections", i, "subtitle"], v)}
                style={{ fontFamily: bodyFont, color: "#52525b", fontSize: 17, marginTop: 10 }} />
            </div>
            <div style={gridCols(3)}>
              {(s.items || []).map((it, j) => (
                <div key={j} className="rounded-2xl p-6" style={{ background: "#fff", border: `1px solid ${secondary}` }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: accent }}>
                    <Check size={18} color="#fff" />
                  </div>
                  <EditableText as="h3" editable={editable} value={it.name} onCommit={(v) => patch(["sections", i, "items", j, "name"], v)}
                    style={{ fontFamily: headingFont, color: text, fontSize: 19, fontWeight: 600 }} />
                  <EditableText as="p" editable={editable} value={it.description} onCommit={(v) => patch(["sections", i, "items", j, "description"], v)}
                    style={{ fontFamily: bodyFont, color: "#52525b", fontSize: 15, marginTop: 8, lineHeight: 1.6 }} />
                  {it.price && <div className="mt-4 text-sm font-semibold" style={{ color: accent, fontFamily: bodyFont }}>{it.price}</div>}
                </div>
              ))}
            </div>
          </>
        ), { background: bg });
      case "about":
        return sectionWrap(i, (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 28 : 56, alignItems: "center" }}>
            <img src={imgs[1] || imgs[0]} onError={isOwnedSlot(imgs[1] ? 1 : 0) ? undefined : onImgError} alt="about" className="w-full object-cover rounded-2xl" style={{ height: isMobile ? 240 : 380 }} />
            <div>
              <EditableText as="h2" editable={editable} value={s.title} onCommit={(v) => patch(["sections", i, "title"], v)}
                style={{ fontFamily: headingFont, color: text, fontSize: isMobile ? 26 : 38, fontWeight: 700, letterSpacing: "-0.02em" }} />
              <EditableText as="p" editable={editable} value={s.body} onCommit={(v) => patch(["sections", i, "body"], v)}
                style={{ fontFamily: bodyFont, color: "#52525b", fontSize: 16, marginTop: 14, lineHeight: 1.7, whiteSpace: "pre-line" }} />
              <div className="flex flex-col gap-2 mt-6">
                {(s.highlights || []).map((h, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <Check size={18} color={accent} />
                    <span style={{ fontFamily: bodyFont, color: text }} className="text-sm font-medium">{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ), { background: secondary });
      case "reviews":
        return sectionWrap(i, (
          <>
            <EditableText as="h2" editable={editable} value={s.title} onCommit={(v) => patch(["sections", i, "title"], v)}
              className="mb-10" style={{ fontFamily: headingFont, color: text, fontSize: isMobile ? 26 : 38, fontWeight: 700, letterSpacing: "-0.02em" }} />
            <div style={gridCols(3)}>
              {(s.items || []).map((r, j) => (
                <div key={j} className="rounded-2xl p-6" style={{ background: "#fff", border: `1px solid ${secondary}` }}>
                  <Rating n={r.rating || 5} color={accent} />
                  <p style={{ fontFamily: bodyFont, color: text, fontSize: 15, marginTop: 12, lineHeight: 1.6 }}>"{r.text}"</p>
                  <div className="mt-4 text-sm font-semibold" style={{ color: "#52525b", fontFamily: bodyFont }}>{r.author}</div>
                </div>
              ))}
            </div>
          </>
        ), { background: bg });
      case "faq":
        return sectionWrap(i, (
          <div className="max-w-3xl mx-auto">
            <EditableText as="h2" editable={editable} value={s.title} onCommit={(v) => patch(["sections", i, "title"], v)}
              className="mb-8 text-center" style={{ fontFamily: headingFont, color: text, fontSize: isMobile ? 26 : 38, fontWeight: 700, letterSpacing: "-0.02em" }} />
            <div className="flex flex-col gap-3">
              {(s.items || []).map((f, j) => (
                <div key={j} className="rounded-xl p-5" style={{ background: "#fff", border: `1px solid ${secondary}` }}>
                  <div style={{ fontFamily: headingFont, color: text, fontWeight: 600, fontSize: 16 }}>{f.q}</div>
                  <div style={{ fontFamily: bodyFont, color: "#52525b", fontSize: 15, marginTop: 6, lineHeight: 1.6 }}>{f.a}</div>
                </div>
              ))}
            </div>
          </div>
        ), { background: secondary });
      case "cta":
        return (
          <section key={i} style={{ background: primary }}>
            <div className={`mx-auto w-full max-w-4xl ${pad} text-center`} style={{ paddingTop: isMobile ? 48 : 80, paddingBottom: isMobile ? 48 : 80 }}>
              <EditableText as="h2" editable={editable} value={s.headline} onCommit={(v) => patch(["sections", i, "headline"], v)}
                style={{ fontFamily: headingFont, color: "#fff", fontSize: isMobile ? 28 : 42, fontWeight: 700, letterSpacing: "-0.02em" }} />
              <EditableText as="p" editable={editable} value={s.subheadline} onCommit={(v) => patch(["sections", i, "subheadline"], v)}
                style={{ fontFamily: bodyFont, color: "rgba(255,255,255,.7)", fontSize: 17, marginTop: 14 }} />
              <button style={{ background: "#fff", color: primary, fontFamily: bodyFont }} className="mt-8 px-8 py-3.5 rounded-full font-semibold text-sm">
                {s.button || "Contact Us"}
              </button>
            </div>
          </section>
        );
      case "contact":
        return sectionWrap(i, (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 40 }}>
            <div>
              <EditableText as="h2" editable={editable} value={s.title} onCommit={(v) => patch(["sections", i, "title"], v)}
                style={{ fontFamily: headingFont, color: text, fontSize: isMobile ? 26 : 38, fontWeight: 700, letterSpacing: "-0.02em" }} />
              <div className="flex flex-col gap-4 mt-6">
                {s.phone && <div className="flex items-center gap-3"><Phone size={18} color={accent} /><span style={{ fontFamily: bodyFont, color: text }}>{s.phone}</span></div>}
                {s.email && <div className="flex items-center gap-3"><Mail size={18} color={accent} /><span style={{ fontFamily: bodyFont, color: text }}>{s.email}</span></div>}
                {s.address && <div className="flex items-center gap-3"><MapPin size={18} color={accent} /><span style={{ fontFamily: bodyFont, color: text }}>{s.address}</span></div>}
              </div>
            </div>
            <div className="rounded-2xl p-6" style={{ background: "#fff", border: `1px solid ${secondary}` }}>
              <div className="flex items-center gap-2 mb-4"><Clock size={18} color={accent} /><span style={{ fontFamily: headingFont, color: text, fontWeight: 600 }}>Opening Hours</span></div>
              <div className="flex flex-col gap-2">
                {(s.hours || []).map((h, j) => (
                  <div key={j} className="flex justify-between text-sm" style={{ fontFamily: bodyFont, color: "#52525b" }}>
                    <span>{h.day}</span><span style={{ color: text, fontWeight: 500 }}>{h.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ), { background: bg });
      default:
        return null;
    }
  };

  return (
    <div style={{ background: bg, color: text, fontFamily: bodyFont }}>
      {/* Nav */}
      <div style={{ background: "rgba(255,255,255,.85)", borderBottom: `1px solid ${secondary}`, backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 20 }}>
        <div className={`mx-auto w-full max-w-6xl ${pad} flex items-center justify-between`} style={{ height: 64 }}>
          <div style={{ fontFamily: headingFont, color: text, fontWeight: 700, fontSize: 20 }}>{nav.logo_text || "Business"}</div>
          {!isMobile && (
            <div className="flex items-center gap-6">
              {(nav.links || []).map((l, j) => (
                <span key={j} style={{ fontFamily: bodyFont, color: "#52525b", fontSize: 14 }} className="font-medium">{l.label}</span>
              ))}
            </div>
          )}
          <button style={{ background: primary, color: "#fff", fontFamily: bodyFont }} className="px-4 py-2 rounded-full font-semibold text-sm">{nav.cta || "Contact"}</button>
        </div>
      </div>
      {sections.map((s, i) => renderSection(s, i))}
      <div style={{ background: primary, color: "rgba(255,255,255,.6)" }} className={`${pad} py-8 text-center text-sm`}>
        <span style={{ fontFamily: bodyFont }}>© {new Date().getFullYear()} {nav.logo_text}.{website.remove_badge ? "" : " "}</span>
        {!website.remove_badge && (
          <a
            href={`${backend || ""}/?ref=badge`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="maplift-referral-badge"
            style={{ fontFamily: bodyFont, color: "#fff", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}
            className="transition-opacity hover:opacity-80"
          >
            Website by MapLift
          </a>
        )}
      </div>
    </div>
  );
}
